/**
 * safe-area.js — האזור שבו מותר להדפיס.
 * ⚠️ קוד פיתוח בלבד. לא נוגע ב-Shopify ולא באתר החי.
 *
 * ═══ למה זה קיים ═══
 *
 * המנוע גוזר את צורת הכרית מערוץ האלפא של ההרכבה. כל פיקסל אטום הופך
 * לחלק מהכרית. המשמעות: כיתוב שיוצא מהסילואט לא "חורג מהמסגרת" —
 * הוא *משנה את צורת הכרית עצמה*. אות אחת שגולשת החוצה מייצרת בליטה
 * בתפר.
 *
 * ולכן החסימה כאן היא לא קוסמטית. היא שומרת על צורת המוצר.
 *
 * בנוסף, כרית היא פריט תפור. הדפסה עד קצה הסילואט נופלת על התפר
 * ונבלעת. לכן האזור הבטוח מכווץ פנימה במרווח תפר.
 *
 * ═══ איך ═══
 *
 * 1. מורידים את האלפא של ההרכבה לרשת G×G.
 * 2. Distance transform (chamfer בשני מעברים) — לכל פיקסל, המרחק
 *    המשוער לפיקסל השקוף הקרוב ביותר.
 * 3. בטוח = מרחק ≥ מרווח התפר.
 *
 * chamfer ולא מרחק אוקלידי מדויק: הוא O(n) בשני מעברים, השגיאה
 * מתחת ל-2% עם המשקלים 3/4, וברזולוציה הזו זה בלתי מורגש.
 */
(function (global) {
  'use strict';

  const G = 220;          // רזולוציית רשת הבדיקה
  const ALPHA_MIN = 12;   // מעליו פיקסל נחשב חלק מהכרית

  function makeGrid(sourceCanvas, seamFraction) {
    const c = document.createElement('canvas');
    c.width = c.height = G;
    const x = c.getContext('2d', { willReadFrequently: true });
    x.clearRect(0, 0, G, G);
    x.drawImage(sourceCanvas, 0, 0, G, G);
    const px = x.getImageData(0, 0, G, G).data;

    // ── distance transform ──
    // אתחול: פיקסל שקוף = 0, אטום = אינסוף
    const BIG = 1e9;
    const d = new Float32Array(G * G);
    for (let i = 0, p = 3; i < G * G; i++, p += 4) {
      d[i] = px[p] > ALPHA_MIN ? BIG : 0;
    }

    const A = 3, B = 4;   // משקלי chamfer: ישר / אלכסון
    // מעבר קדימה
    for (let y = 0; y < G; y++) {
      for (let x2 = 0; x2 < G; x2++) {
        const i = y * G + x2;
        if (d[i] === 0) continue;
        let m = d[i];
        if (y > 0) {
          if (x2 > 0)      m = Math.min(m, d[i - G - 1] + B);
                           m = Math.min(m, d[i - G] + A);
          if (x2 < G - 1)  m = Math.min(m, d[i - G + 1] + B);
        }
        if (x2 > 0)        m = Math.min(m, d[i - 1] + A);
        d[i] = m;
      }
    }
    // מעבר אחורה
    for (let y = G - 1; y >= 0; y--) {
      for (let x2 = G - 1; x2 >= 0; x2--) {
        const i = y * G + x2;
        if (d[i] === 0) continue;
        let m = d[i];
        if (y < G - 1) {
          if (x2 < G - 1) m = Math.min(m, d[i + G + 1] + B);
                          m = Math.min(m, d[i + G] + A);
          if (x2 > 0)     m = Math.min(m, d[i + G - 1] + B);
        }
        if (x2 < G - 1)   m = Math.min(m, d[i + 1] + A);
        d[i] = m;
      }
    }

    // המרווח נמדד כשבר מרוחב הכרית, לא בפיקסלים — כדי שיישאר נכון
    // בכל מידה ובכל רזולוציה.
    const seamPx = Math.max(2, seamFraction * G) * A;
    const safe = new Uint8Array(G * G);
    let count = 0;
    for (let i = 0; i < G * G; i++) {
      if (d[i] >= seamPx) { safe[i] = 1; count++; }
    }

    return { safe, size: G, coverage: count / (G * G) };
  }

  /**
   * האם מסכת הכיתוב נכנסת כולה לאזור הבטוח.
   * מקבלת קנבס G×G שבו הכיתוב מצויר לבן על שקוף.
   * מחזירה את מספר הפיקסלים החורגים — 0 = תקין.
   */
  function violations(grid, textCanvas) {
    const x = textCanvas.getContext('2d', { willReadFrequently: true });
    const px = x.getImageData(0, 0, grid.size, grid.size).data;
    let bad = 0;
    for (let i = 0, p = 3; i < grid.size * grid.size; i++, p += 4) {
      if (px[p] > 24 && !grid.safe[i]) bad++;
    }
    return bad;
  }

  /** קו המתאר של האזור הבטוח, לציור רמז עדין ללקוח. */
  function outlinePath(grid, scale) {
    const s = grid.size;
    const path = new Path2D();
    // מעבר על גבולות: פיקסל בטוח שיש לו שכן לא-בטוח הוא על הקו.
    for (let y = 1; y < s - 1; y++) {
      for (let x = 1; x < s - 1; x++) {
        const i = y * s + x;
        if (!grid.safe[i]) continue;
        if (grid.safe[i - 1] && grid.safe[i + 1] && grid.safe[i - s] && grid.safe[i + s]) continue;
        path.rect(x * scale, y * scale, scale, scale);
      }
    }
    return path;
  }

  global.PZSafeArea = { makeGrid, violations, outlinePath, G };
})(window);
